import './ClassListProducts.scss'

import { DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Col, Input, notification, Row, Select, Table } from 'antd'
import DeleteModal from 'components/okulyonetim-admin/delete-modal/DeleteModal'
import { saveAs } from 'file-saver'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'

import AppContext from '../../../../context/AppContext'
import {
  useOkulSinifUrunleriQuery,
  useSinifUrunuSilMutation
} from '../../../../gql/GeneratedGraphqlTypes'
import Spinner from '../../../spinner/Spinner'
import { ReactComponent as AktarIcon } from '../okul-siparis-page/icons/aktar.svg'

const { Option } = Select
const { Search } = Input

const ClassListProducts: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedDurum, setSelectedDurum] = useState<string | undefined>(undefined)
  const { okul } = useContext(AppContext)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const { data, loading, refetch, error } = useOkulSinifUrunleriQuery({
    variables: {
      sube_id: okul?.id,
      arama: searchValue || null,
      sinif_sube_id: selectedDurum ?? null,
    },
    fetchPolicy: 'network-only',
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sinifUrunuSil] = useSinifUrunuSilMutation()

  const showDeleteModal = (ids: string[]) => {
    setDeleteId(ids.join(','))
    setIsDeleteModalVisible(true)
  }

  const handleDelete = async (ids: string) => {
    setSubmitting(true)
    const idList: number[] = ids.split(',').map((id) => Number(id))
    try {
      const response = await sinifUrunuSil({
        variables: {
          id: idList,
        },
      })
      if (response.data?.sinifUrunuSil.success) {
        notification.success({
          message: '',
          description: `${response.data?.sinifUrunuSil?.message}`,
          duration: 6,
        })
        setIsDeleteModalVisible(false)
        setSelectedRowKeys([])

        await refetch()
      } else {
        notification.error({
          message: '',
          description: `${response.data?.sinifUrunuSil?.message}`,
          duration: 6,
        })
      }
    } catch (error) {
      notification.error({
        message: '',
        description: 'Listeyi gönderirken bir hata oluştu.',
        duration: 6,
      })
    } finally {
      setSubmitting(false)
    }
  }
  // Filtreleme değiştikçe veriyi tekrar çek
  useEffect(() => {
    refetch()
  }, [searchValue, selectedDurum, refetch])

  if (loading) {
    return <Spinner showSpinner={loading} color="red" />
  }

  const handleSearch = () => {
    setSearchValue(searchTerm)
  }

  if (submitting) {
    return <Spinner showSpinner={submitting} color="red" />
  }

  if (error) {
    console.error(error)
    navigate('/')
    return null
  }
  const handleFilterChange = (filterType: 'durum', value: string | undefined) => {
    if (value === null) {
      setSelectedDurum(value)
    } else {
      setSelectedDurum(value)
    }
  }

  const filteredData = data?.okulSinifUrunleri?.sinif_urunleri?.filter(Boolean) ?? []
  const totalItems = filteredData.length
  const displayedItems = selectedRowKeys.length
  const siniflarOptions = [
    <Option key="tum" value={null}>
      Tümü
    </Option>,
    ...(data?.okulSinifUrunleri?.kampusler?.map((kampus) => (
      <Option key={kampus?.id} value={kampus?.id}>
        {kampus?.isim}
      </Option>
    )) || []),
  ]
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => a.id - b.id,
      defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
    },
    {
      title: 'Sınıf',
      dataIndex: 'sinif',
      key: 'sinif',
      sorter: (a: any, b: any) => a.sinif.localeCompare(b.sinif),
    },
    {
      title: 'Ders',
      dataIndex: 'ders',
      key: 'ders',
      sorter: (a: any, b: any) => a.ders.localeCompare(b.ders),
    },
    {
      title: 'Barkod',
      dataIndex: 'barkod',
      key: 'barkod',
      sorter: (a: any, b: any) => a.barkod.localeCompare(b.barkod),
    },
    {
      title: 'Ürün',
      dataIndex: 'urun',
      key: 'urun',
      sorter: (a: any, b: any) => a.urun.localeCompare(b.urun),
    },
    { title: 'Sınıf Not', dataIndex: 'sinif_not', key: 'sinif_not' },
    {
      title: 'Adet',
      dataIndex: 'adet',
      key: 'adet',
      sorter: (a: any, b: any) => a.adet - b.adet,
    },
    { title: 'Mevcut', dataIndex: 'mevcut', key: 'mevcut' },
    { title: 'Oluşturma Tarihi', dataIndex: 'olusturma_tarihi', key: 'olusturma_tarihi' },
  ]
  const exportAllToExcel = () => {
    const worksheetData: any[] = []

    const headers = ['id','sinif_id', 'sinif_adi', 'ders_id', 'ders_adi','urun_id','urun_stok_id','urun_adi','adet', 'barkod', 'olusturma_tarihi', 'ogretmen_id', 'ogretmen_adi','ogretmen_soyadi','ogretmen_mail']
    worksheetData.push(headers)

    data?.okulSinifUrunleri?.sinif_urunleri.forEach((sinifUrunler: any) => {
      const transformedData = [
        sinifUrunler.id || '',
        sinifUrunler.sinif_id || '',
        sinifUrunler.sinif || '',
        sinifUrunler.ders_id || '',
        sinifUrunler.ders || '',
        sinifUrunler.urun_id || '',
        sinifUrunler.urun_stok_id || '',
        sinifUrunler.urun || '',
        sinifUrunler.adet || '',
        sinifUrunler.barkod || '',
        sinifUrunler.olusturma_tarihi || '',
        sinifUrunler.ogretmen_id || '',
        sinifUrunler.ogretmen_adi || '',
        sinifUrunler.ogretmen_soyadi || '',
        sinifUrunler.ogretmen_mail || '',
      ]
      worksheetData.push(transformedData)
    })

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

    const colWidths = worksheetData[0].map((_: string, colIndex: number) =>
      Math.max(...worksheetData.map((row) => (row[colIndex] ? row[colIndex].toString().length : 10))),
    )
    worksheet['!cols'] = colWidths.map((width: number) => ({ wch: width }))

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TümVeriler')
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([excelBuffer]), `sinifUrunler.xlsx`)
  }
  return (
    <div className="okul-siniflisteurunleri-page">
      <div className="okul-sepeti-admin-header">
        <h1>Sınıf Listeleri</h1>
        <div className="export-buttons">
          <Button onClick={exportAllToExcel} className="export-button">
            <AktarIcon /> Dışa Aktar{' '}
          </Button>
          <Button onClick={() => navigate('/okulyonetimadmin/dersurunu/ice-aktar')} className="export-button">
            <AktarIcon /> İçe Aktar
          </Button>
        </div>
      </div>
      <Row gutter={8} className="filter-section">
        <Col span={6}>
          <Search
            placeholder="Sınıf Listesi Ara"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={handleSearch}
            allowClear
            enterButton={
              <div className="search-suffix-div">
                Ara <SearchOutlined />{' '}
              </div>
            }
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Kampusler"
            value={selectedDurum}
            onChange={(value) => handleFilterChange('durum', value)}
            allowClear
          >
            {siniflarOptions}
          </Select>
        </Col>
        {/*//todo: range ile tarih te eklenmeli*/}
        {/*<Col span={6}>*/}
        {/*    <RangePicker value={dateRange} onChange={handleDateChange} format="YYYY-MM-DD" />*/}
        {/*</Col>*/}
      </Row>

      <Table
        dataSource={filteredData as object[]}
        columns={columns}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          total: totalItems,
          showTotal: (total) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                style={{
                  display: selectedRowKeys.length === 0 ? 'none' : '',
                }}
                icon={<DeleteOutlined />}
                onClick={() => showDeleteModal(selectedRowKeys.map((id) => id.toString()))}
                type="primary"
                danger
              >
                Seçilen {selectedRowKeys.length} Sınıf Listelerini Sil
              </Button>

              <span style={{ marginLeft: '10px' }}>
                {' '}
                {/* Buton ile metin arasına boşluk koyuyoruz */}
                Seçilen: {displayedItems} / Toplam: {total}
              </span>
            </div>
          ),
          onChange: (page, size) => {
            setCurrentPage(page)
            setPageSize(size)
          },
        }}
        loading={loading}
        rowKey="id"
        className="okul-yonetim-admin-table"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
          ...Selection,
        }}
      />
      <DeleteModal
        title="Seçilen Sınıf Listeleri Sil"
        id={deleteId || ''}
        visible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onDelete={handleDelete}
      />
    </div>
  )
}

export default ClassListProducts
